import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Footer } from '@/components/common/Footer';
import { GlobalNavigationBar } from '@/components/common/GlobalNavigationBar';
import { PageContextProvider, PageContextType } from '@/components/common/PageContext';
import { ToastProps } from '@/components/common/Toast';
import { hydrateAuthVariablesAction } from '@/features/global/actions';
import { setLoggedUserAction } from '@/features/global/auth/authSlice';
import { setShouldUseHistoryNavigationAction } from '@/features/global/globalNavigationBar';
import { initNavigationAction } from '@/features/global/globalNavigationBar/navigation/navigationSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import { isServer } from '@/services/baseService';

import { wrapper } from '../wrapper';
import { PartialDropoffIds, PartialTemplateIds } from './constants';
import * as styles from './index.styles';

const Toast = dynamic<ToastProps>(() => import('@/components/common/Toast').then(mod => mod.Toast), {
  ssr: false,
});

const Partials = (): ReactJSX.Element | null => {
  const router = useRouter();
  const [pageContext, setPageContext] = useState<PageContextType>({ screenName: 'partials' });
  useLayoutEffect(() => {
    // TODO: 추후 myridi 이전시 각 페이지 컴포넌트 내부에서 만들 수 있도록 이동해야 함
    // https://docs.google.com/spreadsheets/d/1nwUKYJACumdyQFTjUtUNiDffQdPuaJzgIDzaTJgLdLM/edit?gid=626343490#gid=626343490
    const mapPathToPageContext: Record<string, typeof pageContext> = {
      '/event': {
        screenName: 'event',
      },
      '/books': {
        screenName: 'bookdetailhome',
      },
      '/cart': {
        screenName: 'cart',
      },
      '/account/myridi': {
        screenName: 'myridi',
        params: {
          menu: 'account_myridi',
        },
      },
      '/library/recents': {
        screenName: 'myridi',
        params: {
          menu: 'library_recents',
        },
      },
      '/wishlist': {
        screenName: 'myridi',
        params: {
          menu: 'wishlist',
        },
      },
      '/reading-note/timeline': {
        screenName: 'myridi',
        params: {
          menu: 'reading_note',
          tab: 'timeline',
        },
      },
      '/reading-note/shelf': {
        screenName: 'myridi',
        params: {
          menu: 'reading_note',
          tab: 'shelf',
        },
      },
      '/notification/new-release/author': {
        screenName: 'myridi',
        params: {
          menu: 'notification_new_release',
          tab: 'author',
        },
      },
      '/notification/new-release/setup': {
        screenName: 'myridi',
        params: {
          menu: 'notification_new_release',
          tab: 'setup',
        },
      },
      '/notification/optout': {
        screenName: 'myridi',
        params: {
          menu: 'notification_optout',
        },
      },
      '/account/modify': {
        screenName: 'myridi',
        params: {
          menu: 'account_modify',
        },
      },
      '/account/devices': {
        screenName: 'myridi',
        params: {
          menu: 'account_devices',
        },
      },
      '/order/history': {
        screenName: 'myridi',
        params: {
          menu: 'order_history',
        },
      },
      '/order/history_cash': {
        screenName: 'myridi',
        params: {
          menu: 'ridicash',
          tab: 'history_cash',
        },
      },
      '/order/pending_virtual_account': {
        screenName: 'myridi',
        params: {
          menu: 'pending_virtual_account',
        },
      },
      '/order/history_point': {
        screenName: 'myridi',
        params: {
          menu: 'ridicash',
          tab: 'history_point',
        },
      },
      '/review': {
        screenName: 'myridi',
        params: {
          menu: 'review',
        },
      },
      '/coupon': {
        screenName: 'myridi',
        params: {
          menu: 'coupon',
          tab: 'usable',
        },
      },
      '/coupon/unusable': {
        screenName: 'myridi',
        params: {
          menu: 'coupon',
          tab: 'unusable',
        },
      },
    };

    const locationPathname = router.asPath;
    if (locationPathname.startsWith('/event')) {
      setPageContext(mapPathToPageContext['/event']);
    } else if (locationPathname.startsWith('/books')) {
      setPageContext(mapPathToPageContext['/books']);
    } else if (locationPathname.startsWith('/cart')) {
      setPageContext(mapPathToPageContext['/cart']);
    } else {
      const nextPageContext = [...Object.entries(mapPathToPageContext)].find(
        ([pathname]) => locationPathname.replace(/\/$/, '') === pathname,
      )?.[1];
      if (nextPageContext) {
        setPageContext(nextPageContext);
      }
    }
  }, [router.asPath]);

  // NOTE:
  // 추후 마이리디 독서노트 페이지 리팩토링시 코드 정리
  useLayoutEffect(() => {
    if (!router.asPath.startsWith('/reading-note')) {
      return () => {};
    }

    const handle = () => {
      requestAnimationFrame(() => {
        setPageContext({
          screenName: 'myridi',
          params: {
            menu: 'reading_note',
            tab: globalThis.location.pathname.replace('/reading-note/', ''),
          },
        });
      });
    };

    window.addEventListener('change-reading-note-tab', handle);

    return () => {
      window.removeEventListener('change-reading-note-tab', handle);
    };
  }, [router.asPath]);

  const dispatch = useAppDispatch();
  const footerPortal = useMemo<HTMLElement | null>(() => {
    if (!isServer()) {
      return document.querySelector(`#${PartialDropoffIds.FOOTER}`);
    }
    return null;
  }, []);

  const [isHydrated, setIsHydrated] = useState(false);
  useIsomorphicLayoutEffect(() => {
    if (footerPortal && !isHydrated) {
      // template으로 생성된 기존 html을 제거 후 portal을 삽입
      // FIXME React가 Portal에 대한 Hydration을 지원해줄 때 이 코드 제거하기 (facebook/react#13097)
      while (footerPortal.firstChild) {
        footerPortal.removeChild(footerPortal.firstChild);
      }

      setIsHydrated(true);
    }
  }, [isHydrated, footerPortal]);

  useEffect(() => {
    /**
     * /partials 내에서 의도되지 않은 next router 의 라우팅 변경을 막기 위한 설정
     * ex. 브라우저의 back / forward 버튼을 통한 라우팅 변경
     * 기존 /partials 내에서의 의도된 라우팅 동작들은 전부 anchor tag만으로 처리하므로, 이로 인해 blocking 되지 않음
     * @see https://nextjs.org/docs/pages/api-reference/functions/use-router#routerbeforepopstate
     */
    router.beforePopState(() => false);

    return () => router.beforePopState(() => true);
  }, [router]);

  useEffect(() => {
    /**
     * books-backend에서 deviceType을 전달하는 대신 CSR에서 초기화
     * https://ridi.slack.com/archives/C01SP5FT727/p1741161563082519
     */
    dispatch(hydrateAuthVariablesAction());
  }, [dispatch]);

  if (isServer()) {
    if (router.query.type === 'script') {
      return <></>;
    }

    return (
      <PageContextProvider value={pageContext}>
        <GlobalNavigationBar css={styles.partialsBooksBackendOverrideStyle} />
        <template id={PartialTemplateIds.FOOTER}>
          <Footer css={styles.partialsBooksBackendOverrideStyle} />
        </template>
      </PageContextProvider>
    );
  }

  return (
    <PageContextProvider value={pageContext}>
      <GlobalNavigationBar css={styles.partialsBooksBackendOverrideStyle} />
      <Toast />
      {isHydrated &&
        footerPortal &&
        createPortal(<Footer css={styles.partialsBooksBackendOverrideStyle} />, footerPortal)}
    </PageContextProvider>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(store => async ({ query }) => {
  await store.dispatch(initNavigationAction({ req: null }));

  const user =
    query.is_login === 'true'
      ? {
          id: '',
          idx: 0,
          email: '',
          isVerifiedAdult: false,
          adultVerification: {
            status: 'NOT_VERIFIED' as const,
          },
        }
      : null;

  store.dispatch(setLoggedUserAction({ loggedUser: user }));
  store.dispatch(setShouldUseHistoryNavigationAction(false));

  return {
    props: {},
  };
});

export default Partials;
